/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
// Imports for external libraries go here.
import React, { FC, useEffect, useMemo, useState } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { Messages, getDateFormatted, getLocalizedDate } from '@marriott/mi-ui-library';
import { isGreaterChinaLocale } from '@marriott/shared/mi-helper-utils';
import clsx from 'clsx';
import { NGSearchField as SearchField, NGDatePicker as DatePicker } from '../../molecules';
import { AlertMessageBodyProps, SearchFormPrimaryBlockProps } from './NGSearchForm.types';
import { useSearchFormStore } from '../../modules/store/searchFormStore';
import { removePeriods } from '../../modules/utils';
import {
  groupPartnersError,
  searchFormClickTrackingLoc,
  travelAgentsError,
  travelAgentsErrorClickTrackingValue,
  groupPartnersErrorClickTrackingValue,
  hardCodedAllLocale,
  localizedFormattingRequiredLocales,
} from '../../constants/lib/constants';
import { useStaticDataContext } from '../../modules/context';
import {
  SearchFormActiveState,
  checkInFutureDateError,
  checkOutFutureDateError,
  checkInPastDateError,
  checkOutPastDateError,
  ADF_SEARCH_CTA_ENDPOINT_URL,
} from '../../constants';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { convertToLowerCase, displaySessionBasedAlerts, returnApplicableAlert } from '../../utils/src/utils';
import { useConditionalPageContextSERP } from '../../hooks';
import { phoenixShopAdvSearchInventoryDate } from '../../molecules/NGSearchField/queries';
import { useApolloClient } from '@apollo/client';
import { useInventoryDateStore } from '../../modules/store/inventoryDateStore';
import moment from 'moment';

export const SearchPrimaryBlock: FC<SearchFormPrimaryBlockProps> = ({
  isDatesSelected,
  currentFormState,
  setCurrentFormState,
  setMobileFormModalState,
  setIsDestinationParsed,
  setIsDatesSelected,
  isDestinationParsed,
  mobileModalHeading,
  mobilePlaceHolderText,
  alertsListArray,
  isMobileForm,
  isSearchFormSticky,
  isAvailabilityScenario,
  blacklist,
  destinationColStyleOverride = false,
  mobileFormModalState: _mobileFormModalState,
  isInModal = false,
  isNextGenHP = false,
  isTransitioningRef,
  nextGenHPMobileCalendarRef: _nextGenHPMobileCalendarRef,
  cameFromFormStateRef,
  onInputFieldsChangeHandle,
}) => {
  /**
   * search primary block or search top block
   * consist of destination, datepicker and button if required
   * we can render any content on top block as per the requirment
   */
  const destination = useSearchFormStore((state: any) => state?.destination);

  useEffect(() => {
    if (destination?.displayText && !isDestinationParsed) {
      setIsDestinationParsed?.(destination.displayText);
    }
  }, [destination?.displayText]);

  const {
    hideDestination,
    hideBrandsField,
    errorFullDateFormat,
    errorTrimmedDateFormat,
    dateFormatLanguageLabel,
    disableSpecialRates,
    searchCTAEndpointURL,
    styleclass,
    enableWeekendSelectorOnMobile = true,
  } = useStaticDataContext();

  const { pageState, isAuthorMode, isBulgariDomain } = useConditionalPageContextSERP();

  const skipQuery = useMemo(() => {
    return !!pageState?.authorMode || !!isAuthorMode;
  }, [pageState, isAuthorMode]);

  const errorMessages = useSearchFormStore((state: any) => state?.errorMessage);
  const errorMessageKey = errorMessages?.errorMessages?.submitSearchFormErrorMessages?.errorMessageKeys[0];
  const client = useApolloClient();
  const inventoryDateStore = useInventoryDateStore();
  const clientEnvVars = useClientEnvVarsStore(state => state.envVarsObject);
  const { ACCEPT_LANGUAGE: currentLocale, REQUEST_ID: requestID } = clientEnvVars;
  const [alertMessage, setAlertMessage] = useState<AlertMessageBodyProps>({});
  const setIsWeekendButtonSelected = useSearchFormStore((state: any) => state.setHotelsSearchformWeekendButtonSelected);
  const isWeekendButtonSelected = useSearchFormStore((state: any) => state.isHotelsSearchformWeekendButtonSelected);

  // Unique modalId per instance
  const datePickerModalId = React.useId();

  /**
   * Reset parent modal styles that were affected by DatePicker's DropdownModal.
   * The DropdownModal component sets height: 100px on ALL .m-dropdown-content elements
   * when child modals open, which breaks the parent search-form-dropdown layout.
   */
  const resetParentModalStyles = () => {
    if (typeof document !== 'undefined') {
      const searchFormDropdown = document.querySelector('.search-form-dropdown');
      if (searchFormDropdown && (searchFormDropdown as HTMLElement).style) {
        (searchFormDropdown as HTMLElement).style.setProperty('height', 'fit-content');
        (searchFormDropdown as HTMLElement).style.setProperty('min-height', '');
        (searchFormDropdown as HTMLElement).style.setProperty('display', 'unset');
      }
    }
  };

  function formatAndSetAlertMessage(errorMsgObj: AlertMessageBodyProps, supplementString?: string) {
    let clonedErrorObject = { ...errorMsgObj };
    if (errorMsgObj?.stringNeedsToBeSplit && supplementString) {
      let formattedErrorMessage = '';
      const errorMessageParts = errorMsgObj?.alertMessage?.split('{X}');
      formattedErrorMessage = errorMessageParts?.[0] + supplementString + errorMessageParts?.[1];
      clonedErrorObject = { ...clonedErrorObject, alertMessage: formattedErrorMessage };
    } else {
      const updatedErrorMessage = (errorMsgObj.alertMessage ?? '') + (supplementString ?? '');
      clonedErrorObject = { ...clonedErrorObject, alertMessage: updatedErrorMessage };
    }
    setAlertMessage(clonedErrorObject);
  }

  // Add dates to the future date errors
  function updateFutureDateErrorMessage(inventoryData: any, errorMsgObj: AlertMessageBodyProps) {
    if (inventoryData && Object.keys(inventoryData).length > 0) {
      const alertMsgCopy = errorMsgObj;
      const date = inventoryData?.advancedReservationDateLimit?.singleDateLimit?.value ?? '';
      const formattedTextDate =
        currentLocale === hardCodedAllLocale.de
          ? removePeriods(getDateFormatted(moment(date), errorFullDateFormat), false)
          : getDateFormatted(moment(date), errorFullDateFormat);
      alertMsgCopy.alertMessage =
        alertMsgCopy?.alertMessage +
        formattedTextDate +
        `${currentLocale === hardCodedAllLocale.en || currentLocale === hardCodedAllLocale.gb ? '.' : ''}`;
      formatAndSetAlertMessage(alertMsgCopy);
    }
  }

  // Get inventory Dates — uses store to deduplicate across components
  async function getInventoryDates(errorMsgObj: AlertMessageBodyProps) {
    // If store already has the data, use it directly
    if (inventoryDateStore.fetchStatus === 'loaded' && inventoryDateStore.singleDateLimit) {
      updateFutureDateErrorMessage(
        {
          advancedReservationDateLimit: {
            singleDateLimit: { value: inventoryDateStore.singleDateLimit },
            groupDateLimit: { value: inventoryDateStore.groupDateLimit },
          },
        },
        errorMsgObj
      );
      return;
    }
    // If another component is already fetching, wait for it
    if (inventoryDateStore.fetchStatus === 'loading') {
      const waitForStore = () =>
        new Promise<void>(resolve => {
          const unsubscribe = useInventoryDateStore.subscribe(state => {
            if (state.fetchStatus === 'loaded' || state.fetchStatus === 'error') {
              unsubscribe();
              resolve();
            }
          });
        });
      await waitForStore();
      const storeState = useInventoryDateStore.getState();
      if (storeState.singleDateLimit) {
        updateFutureDateErrorMessage(
          {
            advancedReservationDateLimit: {
              singleDateLimit: { value: storeState.singleDateLimit },
              groupDateLimit: { value: storeState.groupDateLimit },
            },
          },
          errorMsgObj
        );
      }
      return;
    }
    // Otherwise, fetch it ourselves
    if (!client?.query) {
      return;
    }
    inventoryDateStore.setFetchStatus('loading');
    try {
      const { data } = await client.query({
        query: phoenixShopAdvSearchInventoryDate,
        fetchPolicy: 'network-only',
        context: {
          headers: {
            'x-request-id': requestID,
            'accept-language': currentLocale,
          },
        },
      });
      if (data?.advancedReservationDateLimit?.singleDateLimit?.value) {
        inventoryDateStore.setInventoryDates(
          data.advancedReservationDateLimit.singleDateLimit.value,
          data.advancedReservationDateLimit.groupDateLimit?.value
        );
      } else {
        inventoryDateStore.setFetchStatus('loaded');
      }
      if (data) {
        updateFutureDateErrorMessage(data, errorMsgObj);
      }
    } catch {
      inventoryDateStore.setFetchStatus('error');
    }
  }

  /**
   * Display the error Message if present in the session
   */
  useEffect(() => {
    if (errorMessageKey) {
      const errorMsgObj: AlertMessageBodyProps = displaySessionBasedAlerts(alertsListArray, errorMessageKey);
      const alertCodeFromAEM = errorMsgObj?.alertCode?.trim();
      if (alertCodeFromAEM === checkInFutureDateError || alertCodeFromAEM === checkOutFutureDateError) {
        if (!skipQuery) {
          getInventoryDates(errorMsgObj);
        }
      } else if (alertCodeFromAEM === checkInPastDateError) {
        const currLocale = dateFormatLanguageLabel ?? 'en-gb';
        require(`moment/locale/${currLocale}`);
        moment.locale(currLocale);
        const todaysDate = moment(new Date());
        const formattedDate = localizedFormattingRequiredLocales.includes(currentLocale?.replace('_', '-'))
          ? getLocalizedDate(todaysDate, errorTrimmedDateFormat, currentLocale)
          : convertToLowerCase(getDateFormatted(todaysDate, errorTrimmedDateFormat), currentLocale);
        formatAndSetAlertMessage(errorMsgObj, formattedDate);
      } else if (alertCodeFromAEM === checkOutPastDateError) {
        const currLocale = dateFormatLanguageLabel ?? 'en-gb';
        require(`moment/locale/${currLocale}`);
        moment.locale(currLocale);
        const tmrwsDate = moment(new Date()).add(1, 'days');
        const formattedDate = localizedFormattingRequiredLocales.includes(currentLocale?.replace('_', '-'))
          ? getLocalizedDate(tmrwsDate, errorTrimmedDateFormat, currentLocale)
          : convertToLowerCase(getDateFormatted(tmrwsDate, errorTrimmedDateFormat), currentLocale);
        formatAndSetAlertMessage(errorMsgObj, formattedDate);
      } else if (alertCodeFromAEM === travelAgentsError) {
        errorMsgObj['clickTrackingText'] = travelAgentsErrorClickTrackingValue;
        formatAndSetAlertMessage(errorMsgObj);
      } else if (alertCodeFromAEM === groupPartnersError) {
        errorMsgObj['clickTrackingText'] = groupPartnersErrorClickTrackingValue;
        formatAndSetAlertMessage(errorMsgObj);
      } else {
        formatAndSetAlertMessage(errorMsgObj);
      }
    } else {
      /**
       * Remove the Alert Message when user came back from Serp
       * If there is an Alert present in previous search
       */
      if (!errorMessageKey) {
        formatAndSetAlertMessage({});
      }
    }
  }, [errorMessageKey]);

  useEffect(() => {
    if (errorMessages?.errorMessages?.alertMessage) {
      formatAndSetAlertMessage(errorMessages.errorMessages);
    }
  }, [errorMessages]);

  return (
    <>
      {alertMessage && alertMessage?.alertMessage && (
        <div className={clsx(isAvailabilityScenario ? 'px-2 w-100' : '')}>
          <Messages
            className={clsx('pb-4 mx-0 hotels-form-error-messages', isAvailabilityScenario ? 'pl-lg-5' : '')}
            messageType={alertMessage?.alertType}
            messageHeading={alertMessage?.alertHeading}
            messageText={alertMessage?.alertMessage}
            linkText={alertMessage?.linkText ?? ''}
            AlertLinkTextLabel={alertMessage?.alertLinkTextLabel ?? ''}
            messageLink={alertMessage?.messageLink ?? ''}
            MessageRedirection={alertMessage?.messageRedirection ?? ''}
            clickTrackingText={alertMessage?.clickTrackingText}
            messageContentTabIndex={0}
            messageRole="alert"
          />
        </div>
      )}
      <div
        className={clsx(
          'primary-wrapper',
          'd-lg-flex',
          // NextGen mobile inactive: flex (side by side), NextGen mobile active (in modal): block (stacked)
          // HP mobile: block (stacked)
          isNextGenHP ? 'd-block' : isInModal ? 'd-block' : 'd-flex',
          isGreaterChinaLocale(currentLocale) ? 'cn-primary-wrapper' : '',
          destination?.displayText !== '' ? 'primary-container' : '',
          isNextGenHP && 'nextGenHP',
          disableSpecialRates ? (destinationColStyleOverride ? '' : 'col-lg-8 col-md-12') : ''
        )}
      >
        {hideDestination !== true && (
          <div
            {...{ custom_click_track_value: `${searchFormClickTrackingLoc} | Destination |internal` }}
            className={clsx(
              'standard',
              // NextGen mobile inactive: no col-12 (side by side with dates)
              // NextGen mobile active (in modal) & HP: col-12 (stacked)
              (isNextGenHP || isInModal) && 'col-12',
              'mb-lg-0',
              'pr-0 pl-0',
              // NextGen mobile (outside modal): no padding (full bleed)
              // NextGen mobile (inside modal) & desktop: padding
              // HP: padding on all viewports
              isNextGenHP || isInModal ? 'pl-lg-0' : 'pr-0 pl-0',

              'custom_click_track'
            )}
            onClick={
              isMobileForm
                ? e => {
                    e.stopPropagation();
                    e.preventDefault();

                    // For HP: Don't re-trigger if we're already in DESTINATION_STATE
                    // This prevents the click from firing when closing the destination modal
                    if (isNextGenHP && currentFormState === SearchFormActiveState.DESTINATION_STATE) {
                      return;
                    }

                    // Track where we came from for HP stepper cancel behavior
                    if (isNextGenHP && cameFromFormStateRef) {
                      // eslint-disable-next-line react-compiler/react-compiler -- intentional ref mutation for cross-component state tracking
                      cameFromFormStateRef.current = currentFormState === SearchFormActiveState.FORM_STATE;
                    }
                    setMobileFormModalState?.(true);
                    // NextGen: Go directly to full form modal (no stepper)
                    // HP: Go to destination stepper
                    setCurrentFormState?.(
                      isNextGenHP ? SearchFormActiveState.DESTINATION_STATE : SearchFormActiveState.FORM_STATE
                    );
                  }
                : undefined
            }
            onKeyDown={
              isMobileForm
                ? e => {
                    if (e.code === '13' || e.key === 'Enter') {
                      e.stopPropagation();
                      e.preventDefault();

                      // For HP: Don't re-trigger if we're already in DESTINATION_STATE
                      if (isNextGenHP && currentFormState === SearchFormActiveState.DESTINATION_STATE) {
                        return;
                      }

                      // Track where we came from for HP stepper cancel behavior
                      if (isNextGenHP && cameFromFormStateRef) {
                        // eslint-disable-next-line react-compiler/react-compiler -- intentional ref mutation for cross-component state tracking
                        cameFromFormStateRef.current = currentFormState === SearchFormActiveState.FORM_STATE;
                      }
                      setMobileFormModalState?.(true);
                      // NextGen: Go directly to full form modal (no stepper)
                      // HP: Go to destination stepper
                      setCurrentFormState?.(
                        isNextGenHP ? SearchFormActiveState.DESTINATION_STATE : SearchFormActiveState.FORM_STATE
                      );
                    }
                  }
                : undefined
            }
            style={{
              cursor: isMobileForm ? 'pointer' : 'default',
            }}
          >
            <SearchField
              onInteraction={onInputFieldsChangeHandle}
              onChange={(inputValue: string) => {
                // Note: Removed isTransitioningRef mutation to satisfy React Compiler
                // Transition state should be managed by the parent component
                setIsDestinationParsed?.(inputValue);

                // NextGen: Stay in FORM_STATE (no stepper flow)
                // HP: Go to calendar if dates not selected, then to full form
                if (isNextGenHP && isMobileForm) {
                  // HP mobile stepper flow: Destination → Date Picker → Search Form
                  // Always go to calendar state to allow date selection/editing

                  // Note: Removed nextGenHPMobileCalendarRef mutations for React Compiler compatibility
                  // Protection logic should be handled by parent component state

                  setCurrentFormState?.(SearchFormActiveState.CALENDAR_STATE);
                  // Ensure modal is open so dates component renders in proper modal context
                  setMobileFormModalState?.(true);

                  // Extended timeout to prevent race conditions
                  setTimeout(() => {
                    if (
                      isTransitioningRef &&
                      typeof isTransitioningRef === 'object' &&
                      'current' in isTransitioningRef
                    ) {
                      setTimeout(() => {
                        // Note: Removed isTransitioningRef mutation
                      }, 0);
                    }
                  }, 300);
                  return; // Early return to prevent further processing
                } else if (isNextGenHP) {
                  // HP desktop: Use existing logic
                  if (!isDatesSelected) {
                    setCurrentFormState?.(SearchFormActiveState.CALENDAR_STATE);
                    setMobileFormModalState?.(true);
                  } else {
                    // If dates already selected, go directly to full form
                    setCurrentFormState?.(SearchFormActiveState.FORM_STATE);
                    setMobileFormModalState?.(true);
                  }
                }
                // NextGen: Already in FORM_STATE, no transition needed

                // Reset transition flag after a brief delay (only for non-nextGenHP mobile)
                // Note: Removed transition flag mutation
              }}
              onCancel={() => {
                // Don't reset state if we're transitioning to another modal
                if (isTransitioningRef?.current) {
                  return;
                }
                // HP stepper flow: Check if we came from FORM_STATE or compact view
                if (isNextGenHP && isMobileForm) {
                  if (cameFromFormStateRef?.current) {
                    // Came from FORM_STATE - return to it
                    setCurrentFormState?.(SearchFormActiveState.FORM_STATE);
                    setMobileFormModalState?.(true);
                  } else {
                    // Came from compact view - close everything
                    setCurrentFormState?.(SearchFormActiveState.CLOSED_STATE);
                    setMobileFormModalState?.(false);
                  }
                  return;
                }
                // NextGen standard flow (unchanged)
                // If we're inside the FORM_STATE modal, return to it instead of closing
                if (isInModal) {
                  setCurrentFormState?.(SearchFormActiveState.FORM_STATE);
                  setMobileFormModalState?.(true);
                } else {
                  // Otherwise, close everything (coming from compact view)
                  setCurrentFormState?.(SearchFormActiveState.CLOSED_STATE);
                  setMobileFormModalState?.(false);
                }
              }}
              // Show SearchField modal when DESTINATION_STATE is active
              isModalOpen={isMobileForm && currentFormState === SearchFormActiveState.DESTINATION_STATE}
              isDesktopView={!isMobileForm}
              mobileModalHeading={mobileModalHeading}
              mobilePlaceHolderText={mobilePlaceHolderText}
              isMobileForm={isMobileForm}
              blacklist={blacklist}
              isNextGenHP={isNextGenHP}
            />
          </div>
        )}
        <div
          className={clsx(
            'standard',
            // NextGen mobile inactive: no col-12 (side by side with destination)
            // NextGen mobile active (in modal) & HP: col-12 (stacked)
            isNextGenHP || isInModal ? 'pr-lg-0 pl-lg-0' : 'pr-0 pl-0',
            'mb-lg-0',
            // NextGen: Always show dates on mobile (no stepper flow)
            // HP: Hide unless in modal or CALENDAR_STATE (stepper flow)
            isNextGenHP && !isInModal && currentFormState !== SearchFormActiveState.CALENDAR_STATE && 'd-none',
            'd-lg-block',
            disableSpecialRates
              ? destinationColStyleOverride
                ? ''
                : 'col-lg-6 col-md-12'
              : hideDestination === true && hideBrandsField === true
              ? 'col-lg-7'
              : ''
          )}
        >
          <DatePicker
            onInteraction={onInputFieldsChangeHandle}
            modalId={`date-picker-modal-wrapper-${datePickerModalId}`}
            onInputClick={
              isMobileForm
                ? () => {
                    // Track where we came from for HP stepper cancel behavior
                    if (isNextGenHP && cameFromFormStateRef) {
                      // eslint-disable-next-line react-compiler/react-compiler -- intentional ref mutation for cross-component state tracking
                      cameFromFormStateRef.current = currentFormState === SearchFormActiveState.FORM_STATE;
                    }
                    // NextGen outside modal: Go directly to full form modal
                    // NextGen inside modal: Open calendar
                    // HP: Go to calendar stepper

                    setCurrentFormState?.(SearchFormActiveState.CALENDAR_STATE);
                    setMobileFormModalState?.(true);
                  }
                : undefined
            }
            onDatePickerChange={(isDatesAvailable: boolean) => {
              // Set transition flag before changing state
              // Note: Removed transition flag mutation
              setIsDatesSelected?.(isDatesAvailable);

              resetParentModalStyles();

              // After date selection, transition to full form modal
              // Only go back to destination if destination not parsed and not ADF page
              if (!isDestinationParsed && searchCTAEndpointURL !== ADF_SEARCH_CTA_ENDPOINT_URL) {
                setCurrentFormState?.(SearchFormActiveState.DESTINATION_STATE);
                setMobileFormModalState?.(true);
              } else {
                // Normal flow: go to full form after date selection
                setCurrentFormState?.(SearchFormActiveState.FORM_STATE);
                setMobileFormModalState?.(true);
              }
              // Reset transition flag after a brief delay
              setTimeout(() => {
                // Note: Removed isTransitioningRef mutation for React Compiler compatibility
                // Transition state should be managed by parent component
              }, 200);
            }}
            onDatePickerCancel={() => {
              // Don't reset state if we're transitioning to another modal
              if (isTransitioningRef?.current) {
                return;
              }

              if (isInModal) {
                resetParentModalStyles();
              }

              // HP stepper flow: Check if we came from FORM_STATE or compact view
              if (isNextGenHP && isMobileForm) {
                if (cameFromFormStateRef?.current) {
                  // Came from FORM_STATE - return to it
                  setCurrentFormState?.(SearchFormActiveState.FORM_STATE);
                  setMobileFormModalState?.(true);
                } else {
                  // Came from compact view - close everything
                  setCurrentFormState?.(SearchFormActiveState.CLOSED_STATE);
                  setMobileFormModalState?.(false);
                }
                return;
              }

              // NextGen standard flow (unchanged)
              // If we're inside the FORM_STATE modal, return to it instead of closing
              if (isInModal) {
                setCurrentFormState?.(SearchFormActiveState.FORM_STATE);
                setMobileFormModalState?.(true);
              } else {
                // Otherwise, close everything (coming from compact view)
                setCurrentFormState?.(SearchFormActiveState.CLOSED_STATE);
                setMobileFormModalState?.(false);
              }
            }}
            setDefaultDate={true}
            isMobileForm={isMobileForm}
            // Show DatePicker modal when CALENDAR_STATE is active (mobile only - desktop manages its own state)
            // For HP: Show modal from outside instance during stepper flow (isInModal=false)
            // For NextGen standard: Only show from inside instance (isInModal=true)
            isModalOpen={
              isMobileForm && (isNextGenHP || isInModal)
                ? currentFormState === SearchFormActiveState.CALENDAR_STATE
                : undefined
            }
            isSearchFormSticky={isSearchFormSticky}
            setIsWeekendButtonSelected={setIsWeekendButtonSelected}
            isWeekendButtonSelected={isWeekendButtonSelected}
            enableWeekendSelectorOnMobile={enableWeekendSelectorOnMobile}
            // NextGen mobile: only apply modal class when inside modal (isInModal)
            // HP mobile: always apply modal class
            inputFieldWrapperClassName={
              isMobileForm && (isNextGenHP || isInModal) ? 'main-mobile-modal-datepicker-field' : undefined
            }
            isNextGenHP={isNextGenHP}
            isInModal={isInModal}
          />
        </div>
      </div>
    </>
  );
};
